import { createI18n } from 'vue-i18n';
import { ref } from 'vue';

// Every file in src/locales/<lang>/*.json is a top-level namespace, e.g. locales/en/landing.json -> $t('landing.…')
function load() {
  const ctx = require.context('../locales', true, /\.json$/);
  const messages = {};
  ctx.keys().forEach((key) => {
    const m = key.match(/^\.\/([a-z]{2})\/([\w-]+)\.json$/);
    if (!m) return;
    const [, lang, ns] = m;
    messages[lang] = messages[lang] || {};
    messages[lang][ns] = ctx(key);
  });
  return messages;
}

export const SUPPORTED = ['en', 'de'];

// Preference: 'auto' (follow the browser, default) or an explicit language chosen in Settings.
function stored() {
  try {
    const saved = localStorage.getItem('lang');
    if (SUPPORTED.includes(saved)) return saved;
  } catch (e) { /* storage can be blocked */ }
  return 'auto';
}

// First supported language in the browser's preference list, e.g. ['de-AT', 'en'] -> 'de'
function fromBrowser() {
  const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || 'en'];
  for (const l of list) {
    const code = String(l).slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(code)) return code;
  }
  return 'en';
}

export const preference = ref(stored());

// The language lives in the URL (/en/..., /de/...) so search engines get one URL per language.
// This is the language for a visit without a prefix (for example "/"): the saved choice, else the browser's.
export function preferredLocale() {
  return preference.value === 'auto' ? fromBrowser() : preference.value;
}

function fromPath() {
  const m = window.location.pathname.match(/^\/(en|de)(\/|$)/);
  return m ? m[1] : null;
}

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: fromPath() || preferredLocale(),
  fallbackLocale: 'en',
  messages: load(),
});

function apply(lang) {
  i18n.global.locale.value = lang;
  document.documentElement.setAttribute('lang', lang);
}

// Used by the router: the URL decides the language of the page being shown.
export function applyLocale(lang) {
  if (SUPPORTED.includes(lang) && i18n.global.locale.value !== lang) apply(lang);
}

// setLocale('auto' | 'en' | 'de')
export function setLocale(choice) {
  if (choice !== 'auto' && !SUPPORTED.includes(choice)) return;
  preference.value = choice;
  try {
    if (choice === 'auto') localStorage.removeItem('lang');
    else localStorage.setItem('lang', choice);
  } catch (e) { /* ignore */ }
  apply(choice === 'auto' ? fromBrowser() : choice);
}

// In auto mode, follow changes of the browser language
window.addEventListener('languagechange', () => {
  if (preference.value === 'auto') apply(fromBrowser());
});

document.documentElement.setAttribute('lang', i18n.global.locale.value);

export default i18n;
